import { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import HttpService from "@/hooks/Https-services";
import { APIRoutes } from "@/utils/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/v2/components/ui/dialog";
import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import { Badge } from "@/v2/components/ui/badge";

function statusVariant(status: string): "default" | "destructive" | "secondary" {
  if (status === "accepted") return "default";
  if (status === "declined") return "destructive";
  return "secondary";
}

/**
 * The one share-a-channel dialog. Used by the Personal Channel page (header
 * button and channel switcher rows) and by the Studio's spotlight cards, so
 * both show the same invite list and the same wording instead of the Studio
 * carrying a stripped-down copy that only took an email.
 *
 * Owns its own fetching: sharing and listing invites are the same two calls
 * wherever the dialog opens from. onShared lets a caller refresh whatever it
 * renders around the dialog (the Studio invalidates its channel queries).
 */
export function ShareChannelDialog({
  open,
  onClose,
  channelId,
  channelName,
  onShared,
}: {
  open: boolean;
  onClose: () => void;
  channelId: number | string;
  channelName: string;
  onShared?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingShares, setPendingShares] = useState<any[]>([]);
  const [isCancelling, setIsCancelling] = useState<number | null>(null);

  async function fetchShares() {
    try {
      const res = await HttpService.get(APIRoutes.GetChannelShares);
      const all = res?.data?.data || [];
      setPendingShares(all.filter((s: any) => s.ChannelID === Number(channelId)));
    } catch {
      // silent - matches v1 behavior
    }
  }

  useEffect(() => {
    if (open && channelId) fetchShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channelId]);

  async function handleShare() {
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await HttpService.post(APIRoutes.ShareChannel, {
        channelId,
        recipientEmail: email.trim(),
      });
      toast.success("Share request sent successfully");
      setEmail("");
      fetchShares();
      onShared?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to send share request");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCancel(shareId: number) {
    setIsCancelling(shareId);
    try {
      await HttpService.deleteData(
        APIRoutes.CancelChannelShare.replace(":shareId", String(shareId)),
      );
      toast.success("Share request cancelled");
      fetchShares();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to cancel share request");
    } finally {
      setIsCancelling(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share "{channelName}"</DialogTitle>
          <DialogDescription>
            Recipients must have an active subscription. They will
            receive an independent copy of this Spotlight.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2">
          <Input
            type="email"
            placeholder="Recipient email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleShare();
            }}
          />
          <Button
            onClick={handleShare}
            disabled={isSubmitting || !email.trim()}
            className="shrink-0 gap-1.5"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Send invite
          </Button>
        </div>

        {pendingShares.length > 0 && (
          <div>
            <h4 className="text-sm font-bold text-slate-900" style={{ marginBottom: "8px" }}>
              Invites
            </h4>
            {/* Card rows rather than a table: an email column would not wrap,
                so long addresses pushed the rows past the dialog's edge. */}
            <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
              {pendingShares.map((share: any) => (
                <div
                  key={share.ID}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200"
                  style={{ padding: "8px 12px" }}
                >
                  <span className="text-sm text-slate-900 break-all min-w-0">
                    {share.RecipientEmail}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge variant={statusVariant(share.Status)}>{share.Status}</Badge>
                    {share.Status === "pending" && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Cancel invite"
                        disabled={isCancelling === share.ID}
                        onClick={() => handleCancel(share.ID)}
                      >
                        {isCancelling === share.ID ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
